export const API_ENDPOINTS = {
  // Endpoints des communautés
  COMMUNITY: {
    // Communautés utilisateur
    getUserCommunities: (userId: string) => `api/communities/communities/of-user/${userId}`,

    // Liste des communautés disponibles
    getAvailableCommunities: 'api/communities/select',

    // Liste des communautés avec filtres et pagination
    getCommunities: 'api/communities/communities',

    // Gestion des participants
    addParticipant: 'api/communities/person/add',

    joinCommunity: (communityId: string) => `api/communities/${communityId}/join`,
    acceptJoinRequest: (requestId: string) => `api/communities/requests/${requestId}/accept`,
    refuseJoinRequest: (requestId: string) => `api/communities/requests/${requestId}/refuse`,

    // Nouveaux endpoints pour les actions de communauté
    acceptCommunityRequest: (communityId: string, requestId: string) => `api/communities/requests/${requestId}/accept`,
    refuseCommunityRequest: (communityId: string, requestId: string) => `api/communities/requests/${requestId}/refuse`,

    // Nouveaux endpoints pour l'ajout de membre avec contexte
    getLinkedPositions: (communityId: number) => `api/communities/${communityId}/linked-positions`,
    addMemberWithContext: 'api/community-members/add-with-context',

    // NOUVEAUX ENDPOINTS POUR LES ÉQUIPES, DÉPARTEMENTS ET PÔLES LIÉS
    getLinkedTeams: (communityId: number) => `api/communities/${communityId}/linked-teams`,
    getLinkedDepartments: (communityId: number) => `api/communities/${communityId}/linked-departments`,
    getLinkedPoles: (communityId: number) => `api/communities/${communityId}/linked-poles`,

    // NOUVEL ENDPOINT POUR LA DEMANDE 2
    getAllRelatedCommunities: (communityId: number) => `api/communities/${communityId}/all-related-communities`,

    // NOUVEL ENDPOINT UNIFIÉ POUR LES OPTIONS DE FILTRAGE
    getFilterOptions: 'api/communities/filter-options'
  },

  // Endpoints des membres de communauté
  COMMUNITY_MEMBERS: {
    // API unifiée pour ajouter/mettre à jour des membres avec contexte
    addOrUpdateWithContext: 'api/communities/community-members/add-or-update-with-context',

    // Récupération des contextes d'un membre pour modification
    getMemberContexts: (memberId: number) => `api/communities/community-members/${memberId}/contexts`,

    // Récupération des données complètes d'un membre pour modification
    getMemberFullData: (memberId: number) => `api/communities/community-members/${memberId}/full-data`,

    // Suppression d'un membre avec contexte
    removeWithContext: (memberId: number) => `api/communities/community-members/${memberId}/remove-with-context`
  },

  // Endpoints des indisponibilités
  UNAVAILABILITY: {
    // Récupérer les indisponibilités avec pagination
    getUnavailabilities: (page: number = 1, limit: number = 10) =>
      `/services/unavailabilities?page=${page}&limit=${limit}`,

    // Créer une indisponibilité
    createUnavailability: '/services/unavailabilities',

    // Supprimer une indisponibilité
    deleteUnavailability: (id: number) => `/services/unavailabilities/${id}`,

    // Mettre à jour une indisponibilité
    updateUnavailability: (id: number) => `api/unavailabilities/${id}`
  },

  // Endpoints des postes
  POSITIONS: {
    // Récupérer tous les postes disponibles
    getPositions: '/services/my-team-positions',

    // Récupérer les postes par campus
    getPositionsByCampus: (campusId: string) => `api/positions/campus/${campusId}`,

    // Nouveaux endpoints pour les postes avec contexte
    getEventTypes: (positionId: number) => `/api/services/positions/${positionId}/event-types`,
    getRelatedCommunities: (positionId: number) => `/api/services/positions/${positionId}/related-communities`
  },

  // Endpoints des équipes
  TEAMS: {
    // Récupérer les communautés liées à une équipe
    getRelatedCommunities: (teamId: number) => `/api/services/teams/${teamId}/related-communities`
  },

  // Endpoints des départements
  DEPARTMENTS: {
    // Récupérer les communautés liées à un département
    getRelatedCommunities: (departmentId: number) => `/api/services/departments/${departmentId}/related-communities`
  },

  // Endpoints des pôles
  POLES: {
    // Récupérer les communautés liées à un pôle
    getRelatedCommunities: (poleId: number) => `/api/services/poles/${poleId}/related-communities`
  },

  // Endpoints des types d'événements
  EVENT_TYPES: {
    // Récupérer tous les types d'événements
    getEventTypes: (params?: { with?: string; per_page?: number; page?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.with) searchParams.append('with', params.with);
      if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
      if (params?.page) searchParams.append('page', params.page.toString());

      const queryString = searchParams.toString();
      return `api/events/types${queryString ? `?${queryString}` : ''}`;
    },

    // Valider les sessions pour un type d'événement
    validateSessions: (typeId: number) => `api/events/types/${typeId}/validate-sessions`,

    // Créer les sessions manquantes pour un type d'événement
    createMissingSessions: (typeId: number) => `api/events/types/${typeId}/create-missing-sessions`
  },

  // Endpoints des sessions
  SESSIONS: {
    // Récupérer les sessions par type d'événement
    getSessionsByEventType: (typeId: number, params?: { with?: string; per_page?: number; page?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.with) searchParams.append('with', params.with);
      if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
      if (params?.page) searchParams.append('page', params.page.toString());

      const queryString = searchParams.toString();
      return `api/events/types/${typeId}/sessions${queryString ? `?${queryString}` : ''}`;
    },

    // Récupérer les sessions disponibles pour un type d'événement
    getAvailableSessions: (typeId: number, params?: { with?: string; per_page?: number; page?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.with) searchParams.append('with', params.with);
      if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
      if (params?.page) searchParams.append('page', params.page.toString());

      const queryString = searchParams.toString();
      return `api/events/types/${typeId}/available-sessions${queryString ? `?${queryString}` : ''}`;
    },

    // Récupérer les sessions pour plusieurs types d'événements
    getSessionsByEventTypes: (params?: { with?: string; per_page?: number; page?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.with) searchParams.append('with', params.with);
      if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
      if (params?.page) searchParams.append('page', params.page.toString());

      const queryString = searchParams.toString();
      return `/events/sessions/select${queryString ? `?${queryString}` : ''}`;
    }
  },

  // Endpoints des préférences de planification
  PREFERENCES: {
    // Récupérer les préférences de l'utilisateur
    getPreferences: (params?: { per_page?: number; page?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
      if (params?.page) searchParams.append('page', params.page.toString());

      const queryString = searchParams.toString();
      return `/services/preferences${queryString ? `?${queryString}` : ''}`;
    },

    // Créer une préférence
    createPreference: '/services/planning-preferences',

    // Mettre à jour une préférence
    updatePreference: (id: number) => `/services/preferences/${id}`,

    // Supprimer une préférence
    deletePreference: (id: number) => `/services/planning-preferences/${id}`
  },

  // Endpoints des événements
  EVENTS: {
    // Récupérer tous les événements
    getEvents: (params?: { with?: string; per_page?: number; page?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.with) searchParams.append('with', params.with);
      if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
      if (params?.page) searchParams.append('page', params.page.toString());

      const queryString = searchParams.toString();
      return `api/events${queryString ? `?${queryString}` : ''}`;
    },

    // Créer un événement
    createEvent: 'api/events',

    // Mettre à jour un événement
    updateEvent: (id: number) => `api/events/${id}`,

    // Supprimer un événement
    deleteEvent: (id: number) => `api/events/${id}`
  },

  // Endpoints des utilisateurs
  USERS: {
    // Récupérer tous les utilisateurs
    getUsers: (params?: { with?: string; per_page?: number; page?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.with) searchParams.append('with', params.with);
      if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
      if (params?.page) searchParams.append('page', params.page.toString());

      const queryString = searchParams.toString();
      return `api/users${queryString ? `?${queryString}` : ''}`;
    },

    // Récupérer un utilisateur par ID
    getUser: (id: number) => `api/users/${id}`,

    // Fiche admin complète (SPEC_07)
    getUserAdminDetails: (id: number) => `api/users/${id}/admin-details`,

    // Créer un utilisateur
    createUser: 'api/users',

    // Mettre à jour un utilisateur
    updateUser: (id: number) => `api/users/${id}`,

    // Supprimer un utilisateur
    deleteUser: (id: number) => `api/users/${id}`,

    // NOUVEAUX ENDPOINTS POUR LA GESTION DES MEMBRES DE FAMILLE
    // Vérifier l'existence d'une personne
    checkPersonExists: '/users/check-person-exists',
    
    // Ajouter des membres de famille existants
    addFamilyMembers: (userId: number) => `/users/${userId}/add-family-members`,
    
    // Créer une nouvelle personne et la rattacher directement a la famille
    createAndAddFamilyMember: (userId: number) => `/users/${userId}/create-family-member`,
    
    // Mettre a jour le role familial d'un membre existant
    updateFamilyMemberStatus: (userId: number, memberId: number) => `/users/${userId}/family-members/${memberId}/status`,
    
    // Créer une demande d'ajout de membre en attente
    createFamilyMemberRequest: '/users/family-member-requests',
    
    // Récupérer les demandes d'ajout en attente
    getPendingFamilyRequests: (userId: number) => `/users/${userId}/pending-family-requests`,
    
    // Approuver une demande d'ajout
    approveFamilyRequest: (requestId: number) => `/users/family-member-requests/${requestId}/approve`,
    
    // Refuser une demande d'ajout
    rejectFamilyRequest: (requestId: number) => `/users/family-member-requests/${requestId}/reject`,

    // Endpoints d'import Excel
    importExcel: 'api/data/users/import/excel',
    getImportStatus: (importId: string) => `api/imports/${importId}/status`
  },

  // Endpoints d'approbation administrative
  APPROVAL: {
    // Récupérer la liste des demandes d'ajout de membres de famille (admin)
    getFamilyRequests: (filters?: { 
      status?: string; 
      search?: string; 
      date_from?: string; 
      date_to?: string; 
      page?: number; 
      per_page?: number 
    }) => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.date_from) params.append('date_from', filters.date_from);
      if (filters?.date_to) params.append('date_to', filters.date_to);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.per_page) params.append('per_page', filters.per_page.toString());

      const queryString = params.toString();
      return `api/users/family-member-requests${queryString ? `?${queryString}` : ''}`;
    },

    // Récupérer une demande spécifique par ID (admin)
    getFamilyRequestById: (requestId: number) => `api/users/family-member-requests/${requestId}`,

    // Approuver une demande d'ajout (admin)
    approveFamilyRequest: (requestId: number) => `api/users/family-member-requests/${requestId}/approve`,

    // Refuser une demande d'ajout (admin)
    rejectFamilyRequest: (requestId: number) => `api/users/family-member-requests/${requestId}/reject`,

    // Récupérer les statistiques des demandes (admin)
    getRequestStats: 'api/users/family-member-requests/stats'
  },

  // Endpoints des indisponibilités administratives
  UNAVAILABILITY_ADMIN: {
    // Récupérer la liste paginée des indisponibilités avec filtres
    getUnavailabilities: (filters?: { 
      search?: string; 
      page?: number; 
      limit?: number;
      campus_ids?: number[];
      pole?: number[];
      department?: number[];
      team?: number[];
    }) => {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      
      if (filters?.campus_ids && filters.campus_ids.length > 0) {
        filters.campus_ids.forEach(id => params.append('campus_ids[]', id.toString()));
      }
      
      if (filters?.pole && filters.pole.length > 0) {
        filters.pole.forEach(id => params.append('pole[]', id.toString()));
      }
      
      if (filters?.department && filters.department.length > 0) {
        filters.department.forEach(id => params.append('department[]', id.toString()));
      }
      
      if (filters?.team && filters.team.length > 0) {
        filters.team.forEach(id => params.append('team[]', id.toString()));
      }

      const queryString = params.toString();
      return `/api/services/admin/unavailabilities${queryString ? `?${queryString}` : ''}`;
    },

    // Exporter les indisponibilités au format XLSX
    exportUnavailabilities: (filters?: {
      search?: string;
      campus_ids?: number[];
      pole?: number[];
      department?: number[];
      team?: number[];
    }) => {
      const params = new URLSearchParams();
      
      if (filters?.search) params.append('search', filters.search);
      
      if (filters?.campus_ids && filters.campus_ids.length > 0) {
        filters.campus_ids.forEach(id => params.append('campus_ids[]', id.toString()));
      }
      
      if (filters?.pole && filters.pole.length > 0) {
        filters.pole.forEach(id => params.append('pole[]', id.toString()));
      }
      
      if (filters?.department && filters.department.length > 0) {
        filters.department.forEach(id => params.append('department[]', id.toString()));
      }
      
      if (filters?.team && filters.team.length > 0) {
        filters.team.forEach(id => params.append('team[]', id.toString()));
      }

      const queryString = params.toString();
      return `/api/services/admin/unavailabilities/export${queryString ? `?${queryString}` : ''}`;
    }
  }
} as const;
